import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Label, LabelsByBoards } from '../../../../../../../common/labels/labelTypes';
import { fetchBoards } from '../../../../../../element/board/boardService';
import {
    selectBoardsToFetchForCurrentPath,
    selectCurrentPathLabels,
} from '../../../../../../element/labels/labelSelectors';
import { mergeCurrentLabels } from '../labelPopupUtils';

type Props = {
    allLabels: Label[];
    searchString: string;
};

type UseLabelListDataReturnType = {
    allLabelsFiltered: Label[];
    currentLabelsFiltered: LabelsByBoards;
    hasSearchResults: boolean;
    isSearching: boolean;
};

const useLabelListData = ({ allLabels, searchString }: Props): UseLabelListDataReturnType => {
    const dispatch = useDispatch();
    const { toFetch, isBoardFetchInProgress } = useSelector(selectBoardsToFetchForCurrentPath);
    const latestCurrentLabels = useSelector(selectCurrentPathLabels);
    const [currentLabels, setCurrentLabels] = useState<LabelsByBoards>(latestCurrentLabels);

    const normalizedSearch = searchString.trim().toLowerCase();
    const isSearching = Boolean(normalizedSearch);

    useEffect(() => {
        if (!toFetch.length) return;

        dispatch(fetchBoards({ boardIds: toFetch }));
    }, [dispatch, toFetch]);

    useEffect(() => {
        if (isBoardFetchInProgress) {
            setCurrentLabels(latestCurrentLabels);
            return;
        }

        setCurrentLabels((previousCurrentLabels) =>
            mergeCurrentLabels(previousCurrentLabels, latestCurrentLabels, allLabels),
        );
    }, [allLabels, isBoardFetchInProgress, latestCurrentLabels]);

    const allLabelsFiltered = useMemo(
        () => allLabels.filter((label) => !isSearching || label.name.toLowerCase().includes(normalizedSearch)),
        [allLabels, isSearching, normalizedSearch],
    );

    const currentLabelsFiltered = useMemo(
        () =>
            currentLabels
                .map(({ id, title, labels }) => ({
                    id,
                    title,
                    labels: labels.filter(
                        (label) => !isSearching || label.name.toLowerCase().includes(normalizedSearch),
                    ),
                }))
                .filter(({ labels }) => labels.length > 0),
        [currentLabels, isSearching, normalizedSearch],
    );

    const hasSearchResults = isSearching ? allLabelsFiltered.length > 0 : currentLabelsFiltered.length > 0;

    return {
        allLabelsFiltered,
        currentLabelsFiltered,
        hasSearchResults,
        isSearching,
    };
};

export default useLabelListData;
